// Carte du monde : parchemin SVG (viewBox 1000 × 1000), cadrage initial sur tous les lieux connus, zoom par boutons et pincement,
// déplacement au doigt, brouillard autour des lieux connus, voisins flous, chemins avec durée, ma position, autres héros, panneau du lieu.
import { useEffect, useRef, useState } from "preact/hooks";
import { HOUR } from "../../shared/types";
import type { Biome, CharacterView, ClientView, Id, LocationKind, MapLocationView } from "../../shared/types";
import "./WorldMap.css";

export interface WorldMapProps { view: ClientView; onTravel: (locationId: Id) => void }

interface Pt { x: number; y: number }
interface Transform { k: number; tx: number; ty: number }
/** Zone visible du cadre, en coordonnées de la carte. */
interface Frame { x0: number; x1: number; y0: number; y1: number }
interface NameLayout { text: string; fs: number; dx: number; below: boolean }
type Tier = "calm" | "watch" | "risk" | "deadly";

const SIZE = 1000;
const K_MIN = 1;
const K_MAX = 3.2;
const K_START = 1.8;
const K_STEP = 1.4;
const PAN_MARGIN = 40;
/** Marge autour des lieux connus lors du cadrage initial (unités de carte). */
const FRAME_MARGIN = 80;
const FOG_RADIUS = 120;
const DRAG_THRESHOLD = 6;
/** décalage de ma pastille quand je suis sur un lieu : elle ne masque ni l'icône ni les sigles des autres */
const ME_AT_PLACE: Pt = { x: -52, y: -4 };
/** Noms des lieux : taille max, plancher avant troncature, longueur max (caractères), largeur max (unités de carte), largeur d'un caractère (em). */
const LABEL_FS = 28;
const LABEL_FS_MIN = 22;
const LABEL_CHARS = 21;
const LABEL_MAX_W = 290;
const LABEL_CHAR_EM = 0.64;
/** Ligne de base du nom : sous le disque, ou au-dessus s'il n'a pas la place dessous (les sigles passent alors dessous). */
const LABEL_BELOW = 62;
const LABEL_ABOVE = -44;
const MARKERS_ABOVE = -62;
const MARKERS_BELOW = 62;

const KIND_ICON: Record<LocationKind, string> = {
  village: "🏘️", hameau: "🏘️", ville: "🏰", chateau: "🏰", foret: "🌲", col: "⛰️", ruines: "🏚️", donjon: "💀",
  tombeau: "💀", grotte: "🕳️", lac: "🌊", port: "🌊", camp: "⛺", tour: "🗼", sanctuaire: "⛩️", clairiere: "🌿",
};
const KIND_LABEL: Record<LocationKind, string> = {
  village: "Village", hameau: "Hameau", ville: "Ville", chateau: "Château", foret: "Forêt", col: "Col",
  ruines: "Ruines", donjon: "Donjon", tombeau: "Tombeau", grotte: "Grotte", lac: "Lac", port: "Port", camp: "Camp",
  tour: "Tour", sanctuaire: "Sanctuaire", clairiere: "Clairière",
};
const BIOME_LABEL: Record<Biome, string> = {
  plaine: "plaine", foret: "forêt", montagne: "montagne", marais: "marais", desert: "désert", cote: "côte",
  toundra: "toundra", souterrain: "souterrain", volcan: "volcan", jungle: "jungle",
};
const TIER_COLOR: Record<Tier, string> = {
  calm: "var(--ok)", watch: "var(--gold)", risk: "var(--ember)", deadly: "var(--danger)",
};

function clampNum(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function tierOf(danger: number): Tier {
  if (danger >= 4) return "deadly";
  if (danger === 3) return "risk";
  if (danger >= 1) return "watch";
  return "calm";
}

function formatHours(h: number): string {
  return `${String(Math.round(h * 10) / 10).replace(".", ",")} h`;
}

function lerp(a: Pt, b: Pt, t: number): Pt {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function travelProgress(departAt: number, arriveAt: number, now: number): number {
  return clampNum((now - departAt) / Math.max(1, arriveAt - departAt), 0, 1);
}

function straightPath(a: Pt, b: Pt): string {
  return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
}

/** Rivière : ligne ondulée, déterministe (le client ne tire jamais au hasard). */
function wavyPath(a: Pt, b: Pt): string {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const waves = Math.max(2, Math.round(len / 70));
  const steps = waves * 8;
  let d = `M ${a.x} ${a.y}`;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const off = Math.sin(t * waves * Math.PI * 2) * 10;
    d += ` L ${(a.x + dx * t + nx * off).toFixed(1)} ${(a.y + dy * t + ny * off).toFixed(1)}`;
  }
  return d;
}

/** Bornes du déplacement : la carte reste couvrant l'écran, à une petite marge près. */
function clampT(t: Transform): Transform {
  const k = clampNum(t.k, K_MIN, K_MAX);
  const lo = SIZE - SIZE * k - PAN_MARGIN;
  return { k, tx: clampNum(t.tx, lo, PAN_MARGIN), ty: clampNum(t.ty, lo, PAN_MARGIN) };
}

/** Zoom en gardant fixe le point d'écran (vx, vy). */
function zoomAt(t: Transform, vx: number, vy: number, nextK: number): Transform {
  const k = clampNum(nextK, K_MIN, K_MAX);
  const cx = (vx - t.tx) / t.k;
  const cy = (vy - t.ty) / t.k;
  return clampT({ k, tx: vx - k * cx, ty: vy - k * cy });
}

function centeredOn(p: Pt, k: number): Transform {
  return clampT({ k, tx: SIZE / 2 - k * p.x, ty: SIZE / 2 - k * p.y });
}

/**
 * Cadrage initial : tous les points (lieux connus, ma position) tiennent dans le cadre, avec une marge.
 * Le zoom ne dépasse jamais K_START : un seul lieu connu donne le cadrage habituel, plusieurs lieux dézooment au besoin.
 */
function framingOf(points: Pt[]): Transform {
  if (points.length === 0) return centeredOn({ x: SIZE / 2, y: SIZE / 2 }, K_START);
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY) + 2 * FRAME_MARGIN;
  const k = clampNum(SIZE / span, K_MIN, K_START);
  return centeredOn({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, k);
}

/** Zone visible du cadre, en coordonnées de la carte. */
function visibleFrame(t: Transform): Frame {
  return { x0: -t.tx / t.k, x1: (SIZE - t.tx) / t.k, y0: -t.ty / t.k, y1: (SIZE - t.ty) / t.k };
}

/**
 * Nom d'un lieu : tronqué au-delà de LABEL_CHARS, plus petit quand il est long, décalé pour rester dans le cadre visible,
 * et passé au-dessus du disque quand il n'a pas la place dessous.
 */
function nameLayout(loc: MapLocationView, frame: Frame): NameLayout {
  const text = loc.name.length > LABEL_CHARS ? `${loc.name.slice(0, LABEL_CHARS - 1).trimEnd()}…` : loc.name;
  const fs = clampNum(LABEL_MAX_W / (text.length * LABEL_CHAR_EM), LABEL_FS_MIN, LABEL_FS);
  const half = (text.length * LABEL_CHAR_EM * fs + 8) / 2;
  let dx = 0;
  if (loc.x >= frame.x0 && loc.x <= frame.x1) {
    const lo = frame.x0 + half;
    const hi = frame.x1 - half;
    dx = (lo <= hi ? clampNum(loc.x, lo, hi) : (frame.x0 + frame.x1) / 2) - loc.x;
  }
  const fitsBelow = loc.y + LABEL_BELOW + fs * 0.25 <= frame.y1;
  const fitsAbove = loc.y + LABEL_ABOVE - fs * 0.75 >= frame.y0;
  return { text, fs, dx, below: fitsBelow || !fitsAbove };
}

/** Ma position sur la carte : le long du chemin pendant un voyage, sinon sur mon lieu. */
function myPosition(view: ClientView, byId: Map<Id, MapLocationView>): Pt {
  const tr = view.character?.travel;
  if (tr) {
    const from = byId.get(tr.from);
    const to = byId.get(tr.to);
    if (from && to) return lerp(from, to, travelProgress(tr.departAt, tr.arriveAt, view.now));
  }
  const here = view.map.locations.find((l) => l.here) ?? view.location;
  return here ? { x: here.x, y: here.y } : { x: SIZE / 2, y: SIZE / 2 };
}

/** Raison pour laquelle le voyage n'est pas possible maintenant, ou null. */
function travelBlock(c: CharacterView, inCombat: boolean, now: number, busy: boolean): string | null {
  if (c.lifeState === "mort") return "Tu n'es plus de ce monde.";
  if (c.lifeState === "agonie") return "Tu es à l'agonie : tiens bon avant de partir.";
  if (c.travel) return `Tu es déjà en route vers ${c.travel.toName}.`;
  if (inCombat) return "Impossible de partir au milieu d'un combat.";
  if (c.lifeState === "endormi" || (c.sleepUntil !== null && c.sleepUntil > now)) return "Tu dors : réveille-toi d'abord.";
  if (busy) return "Le Conteur réfléchit encore à ton action.";
  if (c.energy < 1) return "Plus assez d'énergie : il faut 1 ⚡ pour voyager.";
  return null;
}

function Scenery() {
  return (
    <g>
      <rect x="0" y="0" width={SIZE} height={SIZE} fill="url(#world-map-paper)" />
      <ellipse cx="260" cy="300" rx="220" ry="150" class="world-map-stain" />
      <ellipse cx="760" cy="640" rx="250" ry="170" class="world-map-stain" />
      <ellipse cx="480" cy="520" rx="180" ry="120" class="world-map-stain" />
      <rect x="0" y="0" width={SIZE} height={SIZE} fill="url(#world-map-burn)" />
      <rect x="10" y="10" width={SIZE - 20} height={SIZE - 20} rx="22" class="world-map-rim" filter="url(#world-map-blur)" />
      <g transform="translate(70 930) scale(0.8)">
        <path d="M0 0 L60 -110 L120 0 Z" class="world-map-mountain" />
        <path d="M50 -72 L60 -110 L70 -72 L60 -80 Z" class="world-map-mountain-snow" />
        <path d="M80 0 L130 -80 L180 0 Z" class="world-map-mountain" />
      </g>
      <g transform="translate(210 975) scale(0.55)">
        <path d="M0 0 L60 -110 L120 0 Z" class="world-map-mountain" />
        <path d="M50 -72 L60 -110 L70 -72 L60 -80 Z" class="world-map-mountain-snow" />
      </g>
      <path d="M600 958 q 25 -16 50 0 t 50 0 t 50 0 t 50 0 t 50 0" class="world-map-wave" />
      <path d="M560 982 q 25 -16 50 0 t 50 0 t 50 0 t 50 0 t 50 0 t 50 0" class="world-map-wave" />
      <g transform="translate(880 130)">
        <path d="M0 -70 L14 0 L0 70 L-14 0 Z" class="world-map-compass" />
        <path d="M-70 0 L0 -14 L70 0 L0 14 Z" class="world-map-compass world-map-compass--alt" />
        <circle r="8" class="world-map-compass-hub" />
        <text y="-86" class="world-map-compass-n">N</text>
      </g>
    </g>
  );
}

function Defs({ known }: { known: MapLocationView[] }) {
  return (
    <defs>
      <linearGradient id="world-map-paper" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" class="world-map-paper-a" />
        <stop offset="0.6" class="world-map-paper-a" />
        <stop offset="1" class="world-map-paper-b" />
      </linearGradient>
      <radialGradient id="world-map-burn" cx="0.5" cy="0.5" r="0.72">
        <stop offset="0.62" class="world-map-burn-stop" stop-opacity="0" />
        <stop offset="1" class="world-map-burn-stop" stop-opacity="0.85" />
      </radialGradient>
      <radialGradient id="world-map-hole" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#000" stop-opacity="1" />
        <stop offset="0.55" stop-color="#000" stop-opacity="1" />
        <stop offset="1" stop-color="#000" stop-opacity="0" />
      </radialGradient>
      <mask id="world-map-fog" maskUnits="userSpaceOnUse" x="0" y="0" width={SIZE} height={SIZE}>
        <rect x="0" y="0" width={SIZE} height={SIZE} fill="#fff" />
        {known.map((l) => (
          <circle key={l.id} cx={l.x} cy={l.y} r={FOG_RADIUS} fill="url(#world-map-hole)" />
        ))}
      </mask>
      <filter id="world-map-blur" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="5" />
      </filter>
    </defs>
  );
}

function KnownNode({ loc, name }: { loc: MapLocationView; name: NameLayout }) {
  return (
    <g>
      {name.text !== loc.name ? <title>{loc.name}</title> : null}
      <circle r={34} class="world-map-loc-disc" style={{ stroke: TIER_COLOR[tierOf(loc.danger)] }} />
      <text class="world-map-icon" y={2}>{KIND_ICON[loc.kind]}</text>
      <text
        class="world-map-name"
        x={name.dx}
        y={name.below ? LABEL_BELOW : LABEL_ABOVE}
        style={{ fontSize: `${name.fs}px` }}
      >
        {name.text}
      </text>
    </g>
  );
}

function UnknownNode() {
  return (
    <g>
      <circle r={30} class="world-map-unknown" filter="url(#world-map-blur)" />
      <text class="world-map-unknown-text" filter="url(#world-map-blur)">???</text>
    </g>
  );
}

/** Sigles des autres héros présents, alignés au-dessus du lieu (ou dessous, quand le nom est au-dessus). */
function HeroMarkers({ loc, selfId, below }: { loc: MapLocationView; selfId: Id | undefined; below: boolean }) {
  const list = loc.markers.filter((m) => m.characterId !== selfId);
  const dy = below ? MARKERS_BELOW : MARKERS_ABOVE;
  return (
    <g>
      {list.map((m, i) => (
        <g key={m.characterId} transform={`translate(${(i - (list.length - 1) / 2) * 40} ${dy})`}>
          <title>{m.name}</title>
          <circle r={17} fill={m.color} class="world-map-marker-disc" />
          <text class="world-map-marker-sigil">{m.sigil}</text>
        </g>
      ))}
    </g>
  );
}

interface PlacePanelProps {
  loc: MapLocationView;
  me: CharacterView | null;
  inCombat: boolean;
  now: number;
  onTravel: (locationId: Id) => void;
  onClose: () => void;
}

function PlacePanel({ loc, me, inCombat, now, onTravel, onClose }: PlacePanelProps) {
  const known = loc.known;
  const others = loc.markers.filter((m) => m.characterId !== me?.id).map((m) => m.name);
  const peopleLine = loc.here
    ? (others.length > 0 ? `Avec toi : ${others.join(", ")}.` : "Tu es seul ici.")
    : (others.length > 0 ? `Héros présents : ${others.join(", ")}.` : "Aucun héros en vue.");
  const blocked = me && !loc.here && loc.travelHours !== null ? travelBlock(me, inCombat, now, me.busy) : null;

  return (
    <article class="card card-parchment world-map-panel" aria-live="polite">
      <div class="world-map-panel-head">
        <span class="world-map-panel-icon" aria-hidden="true">{known ? KIND_ICON[loc.kind] : "❔"}</span>
        <div>
          <h3 class="world-map-panel-title">{known ? loc.name : "???"}</h3>
          <p class="world-map-panel-meta">
            {known ? `${KIND_LABEL[loc.kind]} · ${BIOME_LABEL[loc.biome]}` : "Lieu inconnu"}
          </p>
        </div>
        <button type="button" class="icon-btn world-map-panel-close" aria-label="Fermer" onClick={onClose}>×</button>
      </div>
      {known ? (
        <p class="world-map-panel-danger" aria-label={`Danger ${loc.danger} sur 5`}>
          {loc.danger > 0 ? `Danger ${"☠️".repeat(loc.danger)}` : "Paisible"}
        </p>
      ) : null}
      <p class="world-map-panel-text">
        {known
          ? (loc.description ?? "Rien d'autre ne se raconte encore de ce lieu.")
          : "Une silhouette se devine au loin, sans nom ni visage. Il faudra s'en approcher pour la voir."}
      </p>
      {known ? <p class="world-map-panel-people">{peopleLine}</p> : null}
      {me ? (
        <div class="world-map-panel-action">
          {loc.here ? <p class="world-map-panel-reason">Tu es déjà ici.</p> : null}
          {!loc.here && loc.travelHours === null ? (
            <p class="world-map-panel-reason">Aucune route directe depuis ta position.</p>
          ) : null}
          {!loc.here && loc.travelHours !== null ? (
            <>
              <button
                type="button"
                class="btn btn-primary world-map-travel-btn"
                disabled={blocked !== null}
                onClick={() => onTravel(loc.id)}
              >
                {`Voyager · ${formatHours(loc.travelHours)} · 1⚡`}
              </button>
              {blocked ? <p class="world-map-panel-reason">{blocked}</p> : null}
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export function WorldMap(props: WorldMapProps) {
  const { view, onTravel } = props;
  const me = view.character;
  const traveling = me?.travel != null;
  const byId = new Map<Id, MapLocationView>(view.map.locations.map((l): [Id, MapLocationView] => [l.id, l]));
  const hereLoc = view.map.locations.find((l) => l.here) ?? null;
  const inCombat = view.combat !== null && view.combat.status === "en_cours";
  const myPos = myPosition(view, byId);
  const mePoint: Pt = !traveling && hereLoc
    ? { x: myPos.x + ME_AT_PLACE.x, y: myPos.y + ME_AT_PLACE.y }
    : myPos;

  const [tf, setTf] = useState<Transform>(() => framingOf([myPos, ...view.map.locations.filter((l) => l.known)]));
  const [selectedId, setSelectedId] = useState<Id | null>(null);
  const [dragging, setDragging] = useState(false);
  const [, setTick] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);
  const pointers = useRef(new Map<number, Pt>());
  const pinchDist = useRef(0);
  const lastMid = useRef<Pt | null>(null);
  const dragStart = useRef<Pt>({ x: 0, y: 0 });
  const moved = useRef(false);

  // Rafraîchit la position du voyage en cours (la pastille avance avec le temps).
  useEffect(() => {
    if (!traveling) return;
    const id = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, [traveling]);

  const pxScale = (): number => {
    const r = svgRef.current?.getBoundingClientRect();
    return r && r.width > 0 ? SIZE / r.width : 1;
  };

  const toBoard = (p: Pt): Pt => {
    const r = svgRef.current?.getBoundingClientRect();
    const s = pxScale();
    return { x: (p.x - (r?.left ?? 0)) * s, y: (p.y - (r?.top ?? 0)) * s };
  };

  const onPointerDown = (e: PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      moved.current = false;
      dragStart.current = { x: e.clientX, y: e.clientY };
    } else if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinchDist.current = Math.hypot(a.x - b.x, a.y - b.y);
      lastMid.current = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      moved.current = true;
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    const svg = svgRef.current;
    if (!prev || !svg) return;
    const cur: Pt = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);
    const scale = pxScale();
    if (pointers.current.size === 1) {
      if (!moved.current) {
        if (Math.hypot(cur.x - dragStart.current.x, cur.y - dragStart.current.y) < DRAG_THRESHOLD) return;
        moved.current = true;
        setDragging(true);
        svg.setPointerCapture(e.pointerId);
      }
      const dx = (cur.x - prev.x) * scale;
      const dy = (cur.y - prev.y) * scale;
      setTf((t) => clampT({ k: t.k, tx: t.tx + dx, ty: t.ty + dy }));
      return;
    }
    // Deux doigts : pincement autour du milieu, qui se déplace aussi.
    const [a, b] = Array.from(pointers.current.values());
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    const mid: Pt = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const prevMid = lastMid.current ?? mid;
    const ratio = pinchDist.current > 0 && d > 0 ? d / pinchDist.current : 1;
    pinchDist.current = d;
    lastMid.current = mid;
    moved.current = true;
    const board = toBoard(mid);
    const panX = (mid.x - prevMid.x) * scale;
    const panY = (mid.y - prevMid.y) * scale;
    setTf((t) => zoomAt({ k: t.k, tx: t.tx + panX, ty: t.ty + panY }, board.x, board.y, t.k * ratio));
  };

  const endPointer = (e: PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) {
      pinchDist.current = 0;
      lastMid.current = null;
    }
    if (pointers.current.size === 0) setDragging(false);
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const board = toBoard({ x: e.clientX, y: e.clientY });
    setTf((t) => zoomAt(t, board.x, board.y, t.k * Math.exp(-e.deltaY * 0.0015)));
  };

  const zoomBy = (factor: number) => setTf((t) => zoomAt(t, SIZE / 2, SIZE / 2, t.k * factor));
  const recenter = () => setTf(centeredOn(myPos, K_START));
  /** Un geste de déplacement ne compte pas comme un toucher de lieu. */
  const tap = (id: Id) => {
    if (!moved.current) setSelectedId(id);
  };

  const selected = selectedId !== null ? (byId.get(selectedId) ?? null) : null;
  const trFrom = me?.travel ? byId.get(me.travel.from) : undefined;
  const trTo = me?.travel ? byId.get(me.travel.to) : undefined;
  const knownLocs = view.map.locations.filter((l) => l.known);
  const statusText = me?.travel
    ? `En route vers ${me.travel.toName} · encore ${formatHours(Math.max(0, me.travel.arriveAt - view.now) / HOUR)}`
    : hereLoc ? `Tu es à ${hereLoc.name}.` : null;

  const edgeEls = view.map.edges.map((edge, i) => {
    const a = byId.get(edge.a);
    const b = byId.get(edge.b);
    if (!a || !b) return null;
    const faint = !a.known && !b.known;
    const d = edge.kind === "riviere" ? wavyPath(a, b) : straightPath(a, b);
    return <path key={`e${i}`} d={d} class={`world-map-edge world-map-edge--${edge.kind}${faint ? " is-faint" : ""}`} />;
  });

  const edgeLabels = view.map.edges.map((edge, i) => {
    const a = byId.get(edge.a);
    const b = byId.get(edge.b);
    if (!a || !b || (!a.known && !b.known)) return null;
    const text = formatHours(edge.hours);
    const w = text.length * 15 + 20;
    return (
      <g key={`t${i}`} transform={`translate(${(a.x + b.x) / 2} ${(a.y + b.y) / 2})`}>
        <rect x={-w / 2} y={-17} width={w} height={34} rx={17} class="world-map-edge-pill" />
        <text class="world-map-edge-label">{text}</text>
      </g>
    );
  });

  const frame = visibleFrame(tf);
  const nodeEls = view.map.locations.map((loc) => {
    const name = loc.known ? nameLayout(loc, frame) : null;
    return (
      <g
        key={loc.id}
        class="world-map-loc"
        role="button"
        tabIndex={0}
        transform={`translate(${loc.x} ${loc.y})`}
        aria-label={loc.known ? `${loc.name}, ${KIND_LABEL[loc.kind]}` : "Lieu inconnu"}
        onClick={() => tap(loc.id)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            tap(loc.id);
          }
        }}
      >
        {loc.id === selectedId ? <circle r={58} class="world-map-select" /> : null}
        {name ? <KnownNode loc={loc} name={name} /> : <UnknownNode />}
        {name ? <HeroMarkers loc={loc} selfId={me?.id} below={name.below} /> : null}
      </g>
    );
  });

  const travelEl = trFrom && trTo ? <path d={straightPath(trFrom, trTo)} class="world-map-travel-line" /> : null;

  const meEl = me ? (
    <g transform={`translate(${mePoint.x} ${mePoint.y})`} role="img" aria-label="Ta position">
      <circle r={44} class="world-map-me-ring" style={{ stroke: me.appearance.color }} />
      <circle r={24} class="world-map-me-disc" style={{ fill: me.appearance.color }} />
      <text class="world-map-me-sigil">{me.appearance.sigil}</text>
    </g>
  ) : null;

  return (
    <section class="world-map">
      {statusText ? <p class="world-map-status">{statusText}</p> : null}
      <div class="world-map-stage" role="group" aria-label="Carte du monde">
        <svg
          ref={svgRef}
          class={`world-map-svg${dragging ? " is-dragging" : ""}`}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onWheel={onWheel}
        >
          <Defs known={knownLocs} />
          <g transform={`translate(${tf.tx} ${tf.ty}) scale(${tf.k})`}>
            <Scenery />
            {edgeEls}
            <rect x="0" y="0" width={SIZE} height={SIZE} class="world-map-fog" mask="url(#world-map-fog)" />
            {edgeLabels}
            {nodeEls}
            {travelEl}
            {meEl}
          </g>
        </svg>
        <button type="button" class="icon-btn world-map-recenter" aria-label="Recentrer sur ma position" onClick={recenter}>
          ⌖
        </button>
        <div class="world-map-controls">
          <button type="button" class="icon-btn world-map-zoom-btn" aria-label="Zoomer" onClick={() => zoomBy(K_STEP)}>+</button>
          <button type="button" class="icon-btn world-map-zoom-btn" aria-label="Dézoomer" onClick={() => zoomBy(1 / K_STEP)}>−</button>
        </div>
      </div>
      {selected ? (
        <PlacePanel
          loc={selected}
          me={me}
          inCombat={inCombat}
          now={view.now}
          onTravel={onTravel}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </section>
  );
}
